import React, { useEffect, useRef } from 'react';

type Widget = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  remove: (id: string) => void;
};
declare global { interface Window { turnstile?: Widget } }
let scriptReady: Promise<void> | undefined;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptReady) {
    scriptReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => { script.remove(); scriptReady = undefined; reject(new Error('Security check could not load. Please refresh and try again.')); };
      document.head.append(script);
    });
  }
  return scriptReady;
}

export default function Turnstile({ siteKey, resetKey, onToken, onError }: {
  siteKey: string; resetKey: number;
  onToken: (token: string | null) => void; onError: (message: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let cancelled = false;
    let widget: string | undefined;
    onToken(null);
    loadScript().then(() => {
      if (cancelled || !container.current || !window.turnstile) return;
      widget = window.turnstile.render(container.current, {
        sitekey: siteKey, action: 'send_code', theme: 'auto',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => { onToken(null); onError('Please refresh the security check and try again.'); },
      });
    }).catch(error => { if (!cancelled) onError(error.message); });
    return () => { cancelled = true; if (widget !== undefined) window.turnstile?.remove(widget); };
  }, [siteKey, resetKey, onToken, onError]);
  return <div ref={container} className="flex justify-center" aria-label="Security check" />;
}
